import React from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Divider,
  Space,
  Typography,
  Spin,
  Popconfirm,
  Modal,
  Tooltip,
  Steps,
  Badge,
  Tag,
  Empty,
  Table,
} from "antd";
import { PageContentLayout, PageHeaderLayout } from "../../../shared/layout";
import {
  UsergroupAddOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import moment from "moment";
import PublishStatusPanel from "../components/PublishStatusPanel";
import CsvFileNameLink from "../components/CsvFileNameLink";
import isLegacyAudience from "../utils/isLegacyAudience";
import parseCsvFileNames from "../utils/parseCsvFileNames";
import { resolveDspName, getDSPDisplayName } from "../utils/dspDisplay";

const { Title, Text } = Typography;
const { Step } = Steps;

// Mirrors the "Data Type" field's labeling elsewhere in this view.
const getPublishDataTypeLabel = (typeOfFirstPartyData) => {
  if (typeOfFirstPartyData === "phones") return "Phone Numbers";
  if (typeOfFirstPartyData === "lookalike") return "Lookalike Audiences";
  if (typeOfFirstPartyData === "deviceIds") return "Device IDs";
  return "Emails";
};


const FirstPartyDataDetailView = ({
  hashedEmailList,
  loading,
  backLoading,
  step2DspModalVisible,
  setStep2DspModalVisible,
  processingModelScore,
  modelScoreResultsModal,
  setModelScoreResultsModal,
  setModelScoreResults,
  modelScoreResults,
  statusLoading,
  statusResultsModal,
  setStatusResultsModal,
  setStatusResults,
  statusResults,
  handleActivateEmails,
  handleGetLatestStatus,
  handleBackNavigation,
  handleDeleteAudience,
  handleProcessModelScore,
  handleRefreshAudience,
  refreshing,
  providersVisible,
  currentOrg,
  isPublishingToNexxen = false,
  publishStatus,
  activationErrorMessage,
  publishQueryErrorMessage,
  hasPublishFailed,
  hasPublishCompleted,
}) => {
  // Title shown on browser tab
  document.title = 'First Party Data Detail View';

  const renderLabelValuePair = (label, val) => (
    <Space direction="vertical">
      <Text strong style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
        {label}
      </Text>
      <Text>{val}</Text>
    </Space>
  );

  // CSV File Name holds one or more published partition filenames,
  // comma-separated (parseCsvFileNames splits/trims/drops empties — never
  // throws). New HEM audiences get one download link per partition; legacy
  // Statara-era audiences (isLegacyAudience) render the raw value as plain
  // text, since their filenames don't exist under hem/<id>/partitions/ and a
  // link would always fail. Kept out of renderLabelValuePair's <Text> wrapper
  // so multiple links can stack instead of being squeezed into one line.
  const renderCsvFileNameValue = () => {
    const csvFileNames = parseCsvFileNames(hashedEmailList && hashedEmailList.csvFileName);

    if (csvFileNames.length === 0) {
      return <Text>-</Text>;
    }

    if (isLegacyAudience(hashedEmailList)) {
      return <Text>{hashedEmailList.csvFileName}</Text>;
    }

    return (
      <Space direction="vertical" size={0}>
        {csvFileNames.map((fileName) => (
          <CsvFileNameLink
            key={fileName}
            hashedEmailListId={hashedEmailList.id}
            orgID={currentOrg && currentOrg.id}
            fileName={fileName}
          />
        ))}
      </Space>
    );
  };

  const DeleteAudienceButton = () => (
    <Popconfirm
      title="Are you sure you want to delete this audience?"
      onConfirm={handleDeleteAudience}
      okText="Yes"
      cancelText="No"
    >
      <Button icon={<DeleteOutlined />}>Delete Audience</Button>
    </Popconfirm>
  );

  const RefreshStatusBanner = () => {
    const refreshStatus = hashedEmailList.refreshStatus;
    const isNexxen = hashedEmailList.dsp && hashedEmailList.dsp.toUpperCase() === "NEXXEN";
    const isActivated = hashedEmailList.stepProgress === 4 || hashedEmailList.activated;
    // REFRESH_COMPLETED means the refresh was queued; counts take 2-3 days to
    // return from Nexxen. Until they do (deviceCount still 0/null) it's still
    // refreshing — only treat it as complete once devices have come back.
    const hasDevices = hashedEmailList.deviceCount > 0;

    if (!isNexxen || !isActivated || !refreshStatus) return null;
    if (refreshStatus === "ACTIVE") return null;

    if (refreshStatus === "REFRESH_COMPLETED" && hasDevices) {
      return (
        <div style={{ padding: "16px", backgroundColor: "#f6ffed", borderRadius: "6px", border: "1px solid #b7eb8f", marginBottom: "8px" }}>
          <div style={{ textAlign: "center", marginBottom: "12px" }}>
            <CheckCircleOutlined style={{ fontSize: "24px", color: "#52c41a", marginBottom: "8px" }} />
            <Text style={{ display: "block", color: "#52c41a", fontWeight: "bold" }}>
              Refresh Complete
            </Text>
            <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
              {hashedEmailList.refreshMessage ||
                "CSVs have been re-submitted to Nexxen. Device counts typically update within 2-3 days."}
            </Text>
          </div>
          <Button
            onClick={handleRefreshAudience}
            loading={refreshing}
            disabled={loading || refreshing}
            block
            icon={<SyncOutlined />}
          >
            Refresh Again
          </Button>
        </div>
      );
    }

    if (refreshStatus === "NEEDS_REFRESH") {
      return (
        <div style={{ padding: "16px", backgroundColor: "#fff7e6", borderRadius: "6px", border: "1px solid #ffd591", marginBottom: "8px" }}>
          <div style={{ textAlign: "center", marginBottom: "12px" }}>
            <WarningOutlined style={{ fontSize: "24px", color: "#fa8c16", marginBottom: "8px" }} />
            <Text style={{ display: "block", color: "#fa8c16", fontWeight: "bold" }}>
              Audience Needs Refresh
            </Text>
            <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
              Device counts have reset to zero in Nexxen. Re-upload the audience CSVs to restore targeting.
            </Text>
          </div>
          <Button
            type="primary"
            onClick={handleRefreshAudience}
            loading={refreshing}
            disabled={loading || refreshing}
            block
            icon={<SyncOutlined />}
            style={{ backgroundColor: "#fa8c16", borderColor: "#fa8c16" }}
          >
            Refresh Audience
          </Button>
        </div>
      );
    }

    if (
      refreshStatus === "REFRESHING" ||
      (refreshStatus === "REFRESH_COMPLETED" && !hasDevices)
    ) {
      return (
        <div style={{ padding: "16px", backgroundColor: "#e6f7ff", borderRadius: "6px", border: "1px solid #91d5ff", marginBottom: "8px" }}>
          <div style={{ textAlign: "center", marginBottom: "12px" }}>
            <SyncOutlined spin style={{ fontSize: "24px", color: "#1890ff", marginBottom: "8px" }} />
            <Text style={{ display: "block", color: "#1890ff", fontWeight: "bold" }}>
              Refresh In Progress
            </Text>
            <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
              CSVs have been re-uploaded to Nexxen. Device counts typically take 2-3 days to update.
            </Text>
          </div>
          <Button
            disabled
            block
            icon={<SyncOutlined spin />}
          >
            Refreshing...
          </Button>
        </div>
      );
    }

    if (refreshStatus === "REFRESH_FAILED") {
      return (
        <div style={{ padding: "16px", backgroundColor: "#fff1f0", borderRadius: "6px", border: "1px solid #ffa39e", marginBottom: "8px" }}>
          <div style={{ textAlign: "center", marginBottom: "12px" }}>
            <ExclamationCircleOutlined style={{ fontSize: "24px", color: "#ff4d4f", marginBottom: "8px" }} />
            <Text style={{ display: "block", color: "#ff4d4f", fontWeight: "bold" }}>
              Refresh Failed
            </Text>
            <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
              {hashedEmailList.refreshMessage || "An error occurred while refreshing the audience. Please try again."}
            </Text>
          </div>
          <Button
            type="primary"
            danger
            onClick={handleRefreshAudience}
            loading={refreshing}
            disabled={loading || refreshing}
            block
            icon={<SyncOutlined />}
          >
            Retry Refresh
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <React.Fragment>
      <PageHeaderLayout
        titleIcon={<UsergroupAddOutlined />}
        title={hashedEmailList.name || 'Audience'}
        rightContent={[
          <Button key="back" onClick={handleBackNavigation} loading={backLoading} icon={<ArrowLeftOutlined />} style={{ marginTop: '10px' }}>
            Back
          </Button>,
        ]}
      />

      <PageContentLayout>
        <Spin spinning={loading}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Audience Details</span>
                {hashedEmailList && hashedEmailList.processedAt && hashedEmailList.createdAt && 
                 hashedEmailList.processedAt !== null &&
                 moment(hashedEmailList.processedAt).valueOf() !== moment(hashedEmailList.createdAt).valueOf() && (
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    Last Updated: {moment(hashedEmailList.processedAt).format('MM/DD/YYYY - h:mm A')}
                  </Text>
                )}
              </div>
            }
            bordered={false}
            style={{ minHeight: "350px" }}
          >
            {hashedEmailList && (
              <Row style={{ height: "100%" }}>
                <Col span={16}>
                  <Row>
                    <Col span={6}>
                      {renderLabelValuePair(
                        hashedEmailList.typeOfFirstPartyData === "phones"
                          ? "Phone Number Hashed Count"
                          : hashedEmailList.typeOfFirstPartyData === "lookalike"
                            ? "Lookalike Hashed Count"
                            : hashedEmailList.typeOfFirstPartyData === "deviceIds"
                              ? "Device ID Count"
                              : "Email Hashed Count",
                        hashedEmailList.emailCount ? Math.ceil(hashedEmailList.emailCount).toLocaleString() : "0"
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair(
                        "Ingested Devices",
                        hashedEmailList.deviceCount ? Math.ceil(hashedEmailList.deviceCount).toLocaleString() : "0"
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair('Match Rate', hashedEmailList.matchRate)}
                    </Col>
                    <Col span={6}>{renderLabelValuePair('Status', hashedEmailList.status)}</Col>
                  </Row>
                  <Row style={{ marginTop: '2em' }}>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'Created Date',
                        hashedEmailList && hashedEmailList.createdAt
                          ? moment(hashedEmailList.createdAt).format('MM/DD/YYYY - hh:mma')
                          : '-'
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair('Advertiser', hashedEmailList.orgName)}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'Data Type',
                        hashedEmailList.typeOfFirstPartyData === "phones"
                          ? "Phone Numbers"
                          : hashedEmailList.typeOfFirstPartyData === "lookalike"
                            ? "Lookalike Audiences"
                            : hashedEmailList.typeOfFirstPartyData === "deviceIds"
                              ? "Device ID"
                              : "Hashed Email"
                      )}
                    </Col>
                    <Col span={6}>
                      <Space direction="vertical">
                        <Text strong style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                          CSV File Name
                        </Text>
                        {renderCsvFileNameValue()}
                      </Space>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '2em' }}>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'DSP Platform',
                        hashedEmailList.dsp ? getDSPDisplayName(hashedEmailList.dsp, providersVisible) : '-'
                      )}
                    </Col>
                    <Col span={6}>
                      {renderLabelValuePair(
                        'Last Refreshed',
                        hashedEmailList && hashedEmailList.lastRefreshedAt
                          ? moment(hashedEmailList.lastRefreshedAt).format('MM/DD/YYYY - hh:mma')
                          : '-'
                      )}
                    </Col>
                    <Col span={6}>
                      {/* Empty column for future expansion */}
                    </Col>
                    <Col span={6}>
                      {/* Empty column for future expansion */}
                    </Col>
                  </Row>
                </Col>
                
                {/* Right Column - Step-Based Actions */}
                <Col span={8}>
                  <div style={{ paddingLeft: "24px", borderLeft: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                      <Title level={5} style={{ margin: 0, marginRight: "8px" }}>Audience Activation Progress</Title>
                      <Badge 
                        count={`${hashedEmailList.activated ? 4 : hashedEmailList.stepProgress || 0}/4`} 
                        style={{ backgroundColor: (hashedEmailList.activated || (hashedEmailList.stepProgress || 0) === 4) ? "#52c41a" : "#1890ff" }}
                      />
                    </div>
                    
                    <Steps
                      direction="vertical"
                      size="small"
                      current={-1}
                    >
                      {[
                        {
                          title: "Upload Hashed Emails",
                          icon: <UploadOutlined />,
                          description: hashedEmailList.typeOfFirstPartyData === "phones"
                            ? "Your phone number list has been uploaded and validated"
                            : hashedEmailList.typeOfFirstPartyData === "lookalike"
                              ? "Your lookalike audience has been uploaded and validated"
                              : hashedEmailList.typeOfFirstPartyData === "deviceIds"
                                ? "Your device ID list has been uploaded and validated"
                                : "Your SHA-256 hashed email list has been uploaded and validated"
                        },
                        {
                          title: "Create Taxonomy & Upload",
                          icon: <CloudUploadOutlined />,
                          description: "Creating the Nexxen taxonomy entry and uploading your audience file to the Nexxen SFTP"
                        },
                        {
                          title: "Waiting for Activation",
                          icon: <ClockCircleOutlined />,
                          description: "Nexxen is ingesting your audience. This typically completes within 24 hours"
                        },
                        {
                          title: "Activated",
                          icon: <CheckCircleOutlined />,
                          description: "Your audience is live in Nexxen and refreshes automatically every 60 days"
                        }
                      ].map((item, index) => {
                        const stepProgress = hashedEmailList.stepProgress || 0;
                        const status = hashedEmailList.status;
                        
                        // Determine completion based on business logic
                        let isCompleted = false;
                        let isCurrent = false;
                        
                        if (status === "WAITING_FOR_ACTIVATION") {
                          // When waiting for activation, only steps 0 and 1 are completed
                          // Step 2 (Waiting for Activation) is the current step being processed
                          isCompleted = index < 2; // Only Upload Hashed Emails and Create Taxonomy & Upload are completed
                          isCurrent = index === 2; // Waiting for Activation is the current step
                        } else if (status === "ACTIVATED" || stepProgress === 4 || hashedEmailList.activated) {
                          // All steps are completed when activated (using new status field, stepProgress, or legacy activated field)
                          isCompleted = true;
                          isCurrent = false;
                        } else {
                          // Normal progression logic
                          isCompleted = stepProgress > index;
                          isCurrent = stepProgress === index;
                        }
                        
                        return (
                          <Step 
                            key={item.title}
                            title={item.title}
                            description={item.description}
                            icon={
                              isCompleted ? (
                                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                              ) : (
                                React.cloneElement(item.icon, { 
                                  style: { 
                                    color: isCurrent ? "#1890ff" : "#bfbfbf",
                                    fontSize: "20px"
                                  } 
                                })
                              )
                            }
                          />
                        );
                      })}
                    </Steps>
                    
                    <div style={{ marginTop: "20px" }}>
                      {isPublishingToNexxen ? (
                        // Live progress for the publish that was just
                        // triggered from this page — reuses the same panel
                        // the create wizard's Step 2 renders, driven by the
                        // same getHashedEmailPublishStatus poll. Replaces the
                        // step-based action buttons below (including the
                        // "Publish to Nexxen" button) until the publish
                        // reaches a terminal state; the Steps list above is
                        // untouched.
                        <PublishStatusPanel
                          audienceName={hashedEmailList.name}
                          recordCount={hashedEmailList.emailCount}
                          dataTypeLabel={getPublishDataTypeLabel(hashedEmailList.typeOfFirstPartyData)}
                          publishStatus={publishStatus}
                          uploadErrorMessage={activationErrorMessage}
                          queryErrorMessage={publishQueryErrorMessage}
                          hasFailed={hasPublishFailed}
                          hasCompleted={hasPublishCompleted}
                        />
                      ) : (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {hashedEmailList.stepProgress === 1 && (
                          <Button
                            type="primary"
                            onClick={() => setStep2DspModalVisible(true)}
                            disabled={loading}
                            icon={<CloudUploadOutlined />}
                            block
                          >
                            Publish to Nexxen
                          </Button>
                        )}
                        
                        {hashedEmailList.stepProgress === 2 && hashedEmailList.status !== "No Action" && (
                          <>
                            {/* Show View Model Score Result button for lookalike audiences */}
                            {hashedEmailList.typeOfFirstPartyData === "lookalike" && (
                              <>
                                <Button
                                  type="primary"
                                  onClick={handleProcessModelScore}
                                  disabled={loading || processingModelScore || statusLoading}
                                  loading={processingModelScore}
                                  block
                                  style={{ marginBottom: "8px" }}
                                  icon={<ThunderboltOutlined />}
                                >
                                  {hashedEmailList.uploadedAt && hashedEmailList.processedAt ? `View Model Score Result` : `Get Model Score Latest Status`}
                                </Button>
                                {/* Get Latest Status is the only route from step 2 to step 3. It
                                    must not depend on the model score button having stamped
                                    uploadedAt/processedAt first: that ordering deadlocked the step,
                                    since the stamp comes from the very call this button gates. */}
                                <Tooltip
                                  title="Check the latest status of this audience to see if the Nexxen upload is complete and ready for activation."
                                  placement="top"
                                >
                                  <Button
                                    onClick={handleGetLatestStatus}
                                    disabled={loading || statusLoading || processingModelScore}
                                    loading={statusLoading}
                                    block
                                    style={{ marginBottom: "8px" }}
                                    icon={<SyncOutlined />}
                                  >
                                    Get Latest Status
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                            {/* Show Get Latest Status button for non-lookalike audiences */}
                            {hashedEmailList.typeOfFirstPartyData !== "lookalike" && (
                              <Tooltip
                                title="Check the latest status of this audience to see if the Nexxen upload is complete and ready for activation."
                                placement="top"
                              >
                                <Button
                                  onClick={handleGetLatestStatus}
                                  disabled={loading || statusLoading}
                                  loading={statusLoading}
                                  block
                                  style={{ marginBottom: "8px" }}
                                  icon={<SyncOutlined />}
                                >
                                  Get Latest Status
                                </Button>
                              </Tooltip>
                            )}
                          </>
                        )}
                        
                        {hashedEmailList.stepProgress === 3 && hashedEmailList.status !== "No Action" && (
                          <>
                            <div style={{ padding: "16px", backgroundColor: "#fff7e6", borderRadius: "6px", border: "1px solid #ffd591", marginBottom: "8px" }}>
                              <div style={{ textAlign: "center", marginBottom: "12px" }}>
                                <Text style={{ display: "block", color: "#fa8c16", fontWeight: "bold" }}>
                                  Activating in Progress...
                                </Text>
                                <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
                                  Your audience is being processed and will be available for campaign targeting soon.
                                </Text>
                              </div>
                              
                              {/* Status Information */}
                              <div style={{ textAlign: "left", borderTop: "1px solid #ffd591", paddingTop: "12px" }}>
                                <div style={{ marginBottom: "8px" }}>
                                  <Text strong style={{ color: "#ad6800" }}>Status: </Text>
                                  <Text style={{ color: "#ad6800" }}>
                                    {hashedEmailList.status ? hashedEmailList.status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : "Waiting For Activation"}
                                  </Text>
                                </div>
                                
                                {hashedEmailList.dsp && (
                                  <div style={{ marginBottom: "8px" }}>
                                    <Text strong style={{ color: "#ad6800" }}>DSP: </Text>
                                    <Text style={{ color: "#ad6800" }}>
                                      {getDSPDisplayName(hashedEmailList.dsp, providersVisible).toUpperCase()}
                                    </Text>
                                  </div>
                                )}
                                
                                {hashedEmailList.dspSegmentIds && hashedEmailList.dspSegmentIds.length > 0 && (
                                  <>
                                    {hashedEmailList.dspSegmentIds[0].segmentId && (
                                      <div style={{ marginBottom: "8px" }}>
                                        <Text strong style={{ color: "#ad6800" }}>Segment ID: </Text>
                                        <Text style={{ color: "#ad6800", wordBreak: "break-all" }}>
                                          {hashedEmailList.dspSegmentIds[0].segmentId}
                                        </Text>
                                      </div>
                                    )}
                                    {hashedEmailList.dspSegmentIds[0].segmentName && (
                                      <div style={{ marginBottom: "8px" }}>
                                        <Text strong style={{ color: "#ad6800" }}>Segment Name: </Text>
                                        <Text style={{ color: "#ad6800" }}>
                                          {hashedEmailList.dspSegmentIds[0].segmentName}
                                        </Text>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            {/* Show View Model Score Result button for lookalike audiences */}
                            {hashedEmailList.typeOfFirstPartyData === "lookalike" ? (
                              <>
                                <Button
                                  onClick={handleProcessModelScore}
                                  disabled={loading || processingModelScore || statusLoading}
                                  loading={processingModelScore}
                                  block
                                  style={{ marginBottom: "8px" }}
                                  icon={<ThunderboltOutlined />}
                                >
                                  View Model Score Result
                                </Button>
                                <Tooltip
                                  title="Check if this audience has been fully activated and is ready for campaign targeting. This will show if device counts have populated and activation is complete."
                                  placement="top"
                                >
                                  <Button
                                    onClick={handleGetLatestStatus}
                                    disabled={loading || statusLoading || processingModelScore}
                                    loading={statusLoading}
                                    block
                                    style={{ marginBottom: "8px" }}
                                    icon={<SyncOutlined />}
                                  >
                                    Get Latest Status
                                  </Button>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip
                                title="Check if this audience has been fully activated and is ready for campaign targeting. This will show if device counts have populated and activation is complete."
                                placement="top"
                              >
                                <Button
                                  onClick={handleGetLatestStatus}
                                  disabled={loading || statusLoading}
                                  loading={statusLoading}
                                  block
                                  style={{ marginBottom: "8px" }}
                                  icon={<SyncOutlined />}
                                >
                                  Get Latest Status
                                </Button>
                              </Tooltip>
                            )}
                          </>
                        )}
                        
                        {hashedEmailList.status === "No Action" && (
                          <>
                            <div style={{ padding: "16px", backgroundColor: "#f5f5f5", borderRadius: "6px", border: "1px solid #d9d9d9", marginBottom: "8px" }}>
                              <div style={{ textAlign: "center", marginBottom: "12px" }}>
                                <Text style={{ display: "block", color: "#595959", fontWeight: "bold", fontSize: "16px" }}>
                                  No Action Required
                                </Text>
                                <Text style={{ display: "block", fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
                                  This audience is marked as no action. Status updates are not available.
                                </Text>
                              </div>
                            </div>
                          </>
                        )}

                        {(hashedEmailList.stepProgress === 4 || hashedEmailList.activated) && hashedEmailList.status !== "No Action" && (
                          <>
                            <div style={{ padding: "16px", backgroundColor: "#f6ffed", borderRadius: "6px", border: "1px solid #b7eb8f", marginBottom: "8px" }}>
                              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                                <CheckCircleOutlined style={{ fontSize: "24px", color: "#52c41a", marginBottom: "8px" }} />
                                <Text style={{ display: "block", color: "#52c41a", fontWeight: "bold" }}>
                                  Audience Activated!
                                </Text>
                              </div>

                              {/* Activated Audience Details */}
                              <div style={{ textAlign: "left" }}>
                                <Title level={5} style={{ margin: "0 0 12px 0", color: "#52c41a" }}>Activation Details</Title>

                                <div style={{ marginBottom: "8px" }}>
                                  <Text strong style={{ color: "#495057" }}>Status: </Text>
                                  <Text style={{ color: "#6c757d" }}>
                                    {hashedEmailList.status ? hashedEmailList.status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : "Activated"}
                                  </Text>
                                </div>

                                <div style={{ marginBottom: "8px" }}>
                                  <Text strong style={{ color: "#495057" }}>DSP: </Text>
                                  <Text style={{ color: "#6c757d" }}>
                                    {hashedEmailList.dsp && typeof hashedEmailList.dsp === "string" ? getDSPDisplayName(hashedEmailList.dsp, providersVisible).toUpperCase() : "TBD"}
                                  </Text>
                                </div>

                                <div style={{ marginBottom: "8px" }}>
                                  <Text strong style={{ color: "#495057" }}>Segment ID: </Text>
                                  <Text style={{ color: "#6c757d", wordBreak: "break-all" }}>
                                    {hashedEmailList.segmentId || hashedEmailList.dspSegmentIds?.[0]?.segmentId || "TBD"}
                                  </Text>
                                </div>

                                <div style={{ marginBottom: "8px" }}>
                                  <Text strong style={{ color: "#495057" }}>Segment Name: </Text>
                                  <Text style={{ color: "#6c757d" }}>
                                    {hashedEmailList.segmentName || hashedEmailList.dspSegmentIds?.[0]?.segmentName || "TBD"}
                                  </Text>
                                </div>
                              </div>
                            </div>
                            <RefreshStatusBanner />
                            {/* Show View Model Score Result button for lookalike audiences - no Get Latest Status at Step 4 */}
                            {hashedEmailList.typeOfFirstPartyData === "lookalike" && (
                              <Button
                                onClick={handleProcessModelScore}
                                disabled={loading || processingModelScore || statusLoading}
                                loading={processingModelScore}
                                block
                                style={{ marginBottom: "8px" }}
                                icon={<ThunderboltOutlined />}
                              >
                                View Model Score Result
                              </Button>
                            )}
                          </>
                        )}

                        {/* Delete button - always visible but disabled when audience is in progress, activated, or marked as no action */}
                        {(() => {
                          const isDeletionDisabled = hashedEmailList.status === "Waiting For Activation" ||
                                                   hashedEmailList.status === "Activated" ||
                                                   hashedEmailList.status === "WAITING_FOR_ACTIVATION" ||
                                                   hashedEmailList.status === "ACTIVATED" ||
                                                   (hashedEmailList.stepProgress && hashedEmailList.stepProgress >= 3);

                          const tooltipTitle = isDeletionDisabled
                            ? "Cannot delete an audience that is waiting for activation or already activated. This could interrupt the activation process."
                            : "";

                          return (
                            <Tooltip title={tooltipTitle} placement="top">
                              <span style={{ display: "block" }}>
                                <Popconfirm
                                  title="Are you sure you want to delete this audience?"
                                  onConfirm={handleDeleteAudience}
                                  okText="Yes"
                                  cancelText="No"
                                  disabled={isDeletionDisabled}
                                >
                                  <Button
                                    danger
                                    block
                                    disabled={isDeletionDisabled}
                                    icon={<DeleteOutlined />}
                                  >
                                    Delete Audience
                                  </Button>
                                </Popconfirm>
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </Space>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card>
        </Spin>
      </PageContentLayout>

      {/* Model Score Results Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ThunderboltOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
            <span>Model Score Processing Results</span>
          </div>
        }
        open={modelScoreResultsModal}
        onCancel={() => {
          setModelScoreResultsModal(false);
          setModelScoreResults(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setModelScoreResultsModal(false);
            setModelScoreResults(null);
          }} icon={<CloseOutlined />}>
            Close
          </Button>
        ]}
        width={700}
        style={{ top: 20 }}
        bodyStyle={{ padding: '24px' }}
      >
        {modelScoreResults && (() => {
          // Helper function to filter out Job ID from messages
          const filterJobId = (text) => {
            if (!text) return text;
            // Remove patterns like "Job ID: 104" or "Job ID 104" (case insensitive)
            return text.replace(/Job\s*ID\s*:?\s*\d+\.?\s*/gi, '').trim();
          };

          // Helper function to remove duplicate message parts
          const deduplicateMessage = (text) => {
            if (!text) return text;

            // Split by common delimiters that might separate the duplicate (: or newlines)
            const parts = text.split(/:\s*/);

            // If we have exactly 2 parts and they're the same, return just one
            if (parts.length === 2 && parts[0].trim() === parts[1].trim()) {
              return parts[0].trim();
            }

            // Otherwise return the original text
            return text;
          };

          // processModelScore nests job state under `data`. Reading status off
          // the root left it undefined, so neither branch below matched and the
          // modal fell through to the in-progress spinner for every response —
          // including activated audiences. The old message-substring fallback
          // then inverted it the other way, reporting a freshly queued job as
          // complete because "queued successfully" contains "successfully".
          const modelScoreStatus = modelScoreResults.data?.status;

          // alreadyProcessed is checked alongside status so a completed audience
          // still reads as done against a backend that has not picked up the
          // matching job-vocabulary fix yet.
          const isSuccessful =
            modelScoreStatus === 'completed' ||
            modelScoreResults.data?.alreadyProcessed === true;

          const isFailed =
            modelScoreStatus === 'failed' ||
            modelScoreResults.success === false;

          return (
          <div>
            {/* Status Header */}
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: isSuccessful ? '#f6ffed' : (isFailed ? '#fff2f0' : '#e6f7ff'),
              borderRadius: '8px',
              border: `1px solid ${isSuccessful ? '#b7eb8f' : (isFailed ? '#ffccc7' : '#91d5ff')}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                {isSuccessful ? (
                  <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                ) : (isFailed ? (
                  <CloseOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                ) : (
                  <LoadingOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                ))}
                <div>
                  <Text strong style={{
                    fontSize: '16px',
                    color: isSuccessful ? '#52c41a' : (isFailed ? '#ff4d4f' : '#1890ff')
                  }}>
                    {isSuccessful ? 'Processing Completed' :
                     (isFailed ? 'Processing Failed' :
                     'Processing In Progress')}
                  </Text>
                </div>
              </div>
            </div>

            {/* Message Section */}
            {modelScoreResults.message && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status Message
                  </Text>
                </div>
                <div style={{
                  backgroundColor: '#fafafa',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0'
                }}>
                  <Text style={{
                    color: '#595959',
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}>
                    {deduplicateMessage(filterJobId(modelScoreResults.message))}
                  </Text>
                </div>
              </div>
            )}

            {/* Results Data - only show if result object exists */}
            {modelScoreResults.result && modelScoreResults.result.data && (
              <div>
                <Divider orientation="left" style={{ marginTop: 0, marginBottom: '20px' }}>
                  <Text strong style={{ fontSize: '14px' }}>Processing Details</Text>
                </Divider>

                <Row gutter={[16, 16]}>
                  {/* Filtered IDs Count */}
                  {modelScoreResults.result.data.filteredIdsCount !== undefined && (
                    <Col xs={24} sm={12}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: '8px',
                          backgroundColor: '#f0f5ff',
                          border: '1px solid #adc6ff'
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <Text style={{
                            display: 'block',
                            fontSize: '12px',
                            color: '#8c8c8c',
                            marginBottom: '8px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Filtered IDs
                          </Text>
                          <Text strong style={{
                            fontSize: '24px',
                            color: '#1890ff',
                            fontWeight: 600
                          }}>
                            {modelScoreResults.result.data.filteredIdsCount.toLocaleString()}
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  )}

                  {/* Total Processed */}
                  {modelScoreResults.result.data.totalProcessed !== undefined && (
                    <Col xs={24} sm={12}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: '8px',
                          backgroundColor: '#f6ffed',
                          border: '1px solid #b7eb8f'
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <Text style={{
                            display: 'block',
                            fontSize: '12px',
                            color: '#8c8c8c',
                            marginBottom: '8px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Total Processed
                          </Text>
                          <Text strong style={{
                            fontSize: '24px',
                            color: '#52c41a',
                            fontWeight: 600
                          }}>
                            {modelScoreResults.result.data.totalProcessed.toLocaleString()}
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  )}

                  {/* Percentage Kept */}
                  {modelScoreResults.result.data.percentageKept !== undefined && (
                    <Col xs={24} sm={12}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: '8px',
                          backgroundColor: '#fff7e6',
                          border: '1px solid #ffd591'
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <Text style={{
                            display: 'block',
                            fontSize: '12px',
                            color: '#8c8c8c',
                            marginBottom: '8px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Percentage Kept
                          </Text>
                          <Text strong style={{
                            fontSize: '24px',
                            color: '#fa8c16',
                            fontWeight: 600
                          }}>
                            {modelScoreResults.result.data.percentageKept}%
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  )}
                </Row>

                {/* S3 Path - Full width */}
                {modelScoreResults.result.data.s3Path && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        S3 File Path
                      </Text>
                    </div>
                    <div style={{
                      backgroundColor: '#fafafa',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #f0f0f0'
                    }}>
                      <Text
                        code
                        style={{
                          fontSize: '12px',
                          wordBreak: 'break-all',
                          backgroundColor: 'transparent',
                          padding: 0,
                          border: 'none',
                          color: '#262626'
                        }}
                      >
                        {modelScoreResults.result.data.s3Path}
                      </Text>
                    </div>
                  </div>
                )}

                {/* Result Message */}
                {modelScoreResults.result.message && (
                  <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Result Message
                      </Text>
                    </div>
                    <div style={{
                      backgroundColor: '#f0f5ff',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #d6e4ff'
                    }}>
                      <Text style={{
                        color: '#434343',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        fontStyle: 'italic'
                      }}>
                        {deduplicateMessage(filterJobId(modelScoreResults.result.message))}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })()}
      </Modal>

      {/* Status Results Modal */}
      <Modal
        title="Status Update Results"
        open={statusResultsModal}
        onCancel={() => {
          setStatusResultsModal(false);
          setStatusResults(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setStatusResultsModal(false);
            setStatusResults(null);
          }} icon={<CloseOutlined />}>
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {statusResults && (
          <div>
            {/* Audience Details Card */}
            {statusResults.hashedEmailLists && statusResults.hashedEmailLists.length > 0 && (
              <div>
                <Title level={4} style={{ marginBottom: '16px' }}>Audience Details</Title>
                {statusResults.hashedEmailLists.map((audience, index) => {
                  // Helper functions
                  const getStatusColor = (status) => {
                    switch (status) {
                      case "UPLOADED":
                        return "processing";
                      case "WAITING_FOR_ACTIVATION":
                        return "warning";
                      case "ACTIVATED":
                        return "green";
                      default:
                        return "default";
                    }
                  };

                  const getStatusText = (status) => {
                    switch (status) {
                      case "UPLOADED":
                        return "Uploaded";
                      case "WAITING_FOR_ACTIVATION":
                        return "Waiting For Activation";
                      case "ACTIVATED":
                        return "Activated";
                      default:
                        return status || "Unknown";
                    }
                  };
                  
                  // Determine effective status (handle legacy activated field)
                  const effectiveStatus = audience.status || (audience.activated ? "ACTIVATED" : null);
                  
                  // Get DSP info
                  const dspName = resolveDspName(audience);
                  const displayDspName = getDSPDisplayName(dspName, providersVisible);
                  
                  // Get Segment info
                  let segmentId = audience.segmentId;
                  let segmentName = audience.segmentName;
                  if (!segmentId && audience.dspSegmentIds && audience.dspSegmentIds.length > 0) {
                    segmentId = audience.dspSegmentIds[0].segmentId;
                    segmentName = audience.dspSegmentIds[0].segmentName;
                  }
                  
                  // Get created date
                  const createdAt = audience.createdAt || audience.created_at;
                  
                  // Get process result
                  const processResult = audience.processResult;
                  const resultMessage = processResult 
                    ? (typeof processResult === 'string' ? processResult : processResult.message)
                    : null;
                  
                  return (
                    <Card
                      key={audience.id || index}
                      style={{ 
                        marginBottom: '16px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #f0f0f0'
                      }}
                      bodyStyle={{ padding: '24px' }}
                    >
                      {/* Header Section */}
                      <div style={{ marginBottom: '20px' }}>
                        <Row align="middle" justify="space-between">
                          <Col>
                            <Title level={5} style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                              {audience.name}
                            </Title>
                          </Col>
                          <Col>
                            <Space size="middle">
                              <Badge 
                                count={`${audience.stepProgress || 0}/4`} 
                                style={{ backgroundColor: (audience.stepProgress || 0) === 4 ? '#52c41a' : '#1890ff' }}
                              />
                              {effectiveStatus === 'WAITING_FOR_ACTIVATION' ? (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    backgroundColor: '#fa8c16',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: '1px solid #fa8c16',
                                    lineHeight: '20px'
                                  }}
                                >
                                  {getStatusText(effectiveStatus)}
                                </span>
                              ) : (
                                <Tag 
                                  color={getStatusColor(effectiveStatus)} 
                                  style={{ 
                                    fontSize: '12px', 
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    fontWeight: 500
                                  }}
                                >
                                  {getStatusText(effectiveStatus)}
                                </Tag>
                              )}
                            </Space>
                          </Col>
                        </Row>
                      </div>

                      {/* Details Grid */}
                      <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              DSP Platform
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {displayDspName ? displayDspName.toUpperCase() : "N/A"}
                            </Text>
                          </div>
                        </Col>

                        {/* Hide Ingested Devices when stepProgress is 2 or 3 */}
                        {/* Show Ingested Devices when stepProgress is 4 AND status is ACTIVATED */}
                        {(audience.stepProgress !== 2 && audience.stepProgress !== 3) && (
                          <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: '4px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Ingested Devices
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                {audience.devicesCount ? audience.devicesCount.toLocaleString() : (audience.deviceCount ? audience.deviceCount.toLocaleString() : "N/A")}
                              </Text>
                            </div>
                          </Col>
                        )}

                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Created Date
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {createdAt ? moment(createdAt).format("MMM DD, YYYY") : "N/A"}
                            </Text>
                          </div>
                        </Col>
                        
                        <Col xs={24} sm={12} md={8}>
                          <div style={{ marginBottom: '4px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Data Type
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                              {audience.typeOfFirstPartyData === "phones" ? "Phone Numbers" : audience.typeOfFirstPartyData === "lookalike" ? "Lookalike Audiences" : audience.typeOfFirstPartyData === "deviceIds" ? "Device ID" : "Hashed Email"}
                            </Text>
                          </div>
                        </Col>

                        {/* Show Segment Name only when stepProgress is 3 or 4 */}
                        {(audience.stepProgress === 3 || audience.stepProgress === 4) && (
                          <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: '4px' }}>
                              <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Segment Name
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                {segmentName && segmentName.trim() !== "" ? segmentName : "N/A"}
                              </Text>
                            </div>
                          </Col>
                        )}
                      </Row>

                      {/* Segment ID Section (if available) */}
                      {segmentId && segmentId.trim() !== "" && (
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Segment ID
                            </Text>
                          </div>
                          <div style={{ 
                            backgroundColor: '#fafafa', 
                            padding: '12px', 
                            borderRadius: '6px',
                            border: '1px solid #f0f0f0'
                          }}>
                            <Text 
                              code 
                              style={{ 
                                fontSize: '13px',
                                wordBreak: 'break-all',
                                backgroundColor: 'transparent',
                                padding: 0,
                                border: 'none'
                              }}
                            >
                              {segmentId}
                            </Text>
                          </div>
                        </div>
                      )}

                      {/* Message Section (handle new response format) */}
                      {(resultMessage || audience.message) && (
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <Text strong style={{ color: '#8c8c8c', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Status Message
                            </Text>
                          </div>
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '12px', 
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}>
                            <Text style={{ 
                              color: '#666', 
                              fontSize: '13px', 
                              fontStyle: 'italic',
                              lineHeight: '1.5'
                            }}>
                              {audience.message || resultMessage}
                            </Text>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
            
            {(!statusResults.hashedEmailLists || statusResults.hashedEmailLists.length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Empty description="No detailed results available" />
              </div>
            )}
          </div>
        )}
      </Modal>
      
      {/* Publish to Nexxen confirmation modal. Nexxen is the only
          destination now, so there is nothing to pick — this just confirms
          the action before calling activateHashedEmailList directly with
          dsp: "NEXXEN" (bypassing the liquidm-defaulted step2DspProvider
          state, which existed only for the old DSP picker). */}
      <Modal
        title="Publish to Nexxen"
        open={step2DspModalVisible}
        onOk={() => {
          setStep2DspModalVisible(false);
          handleActivateEmails("nexxen");
        }}
        onCancel={() => setStep2DspModalVisible(false)}
        okText="Publish to Nexxen"
        cancelText="Cancel"
      >
        <p>
          This will upload the hashed email list to Nexxen for activation.
          Nexxen typically completes ingestion within 24 hours.
        </p>
      </Modal>
    </React.Fragment>
  );
};

export default FirstPartyDataDetailView;
